// client/src/components/integrations/TikTokConnectButton.jsx
// ========================================================================
// TikTok Connection Button Component
// Handles OAuth popup flow and connection status display
// ========================================================================

import React, { useState, useEffect } from 'react';

/**
 * TikTok Connect Button Component
 * @param {number} tenantId - The tenant ID for connection
 * @param {boolean} enableDirect - Whether to enable direct posting (requires video.publish scope)
 * @param {function} onConnectionChange - Callback when connection status changes
 */
function TikTokConnectButton({ tenantId, enableDirect = false, onConnectionChange }) {
  const [status, setStatus] = useState({ connected: false, loading: true });
  const [statusMessage, setStatusMessage] = useState('');
  
  const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

  // Check connection status on mount and when tenantId changes
  useEffect(() => {
    if (tenantId) {
      checkConnectionStatus();
    }
  }, [tenantId]);

  /**
   * Check if TikTok is connected for this tenant
   */
  const checkConnectionStatus = async () => {
    try {
      setStatus(prev => ({ ...prev, loading: true }));
      
      const response = await fetch(
        `${baseUrl}/api/integrations/tiktok/status?tenantId=${tenantId}`
      );
      const data = await response.json();
      
      setStatus({
        connected: data.connected || false,
        loading: false,
        userInfo: data.userInfo,
        expired: data.expired,
        isDirect: data.isDirect
      });
      
      if (data.connected && data.userInfo?.display_name) {
        setStatusMessage(`Connected as @${data.userInfo.display_name}`);
      } else if (data.connected) {
        setStatusMessage('Connected to TikTok');
      } else {
        setStatusMessage('');
      }
      
      // Notify parent component of connection change
      if (onConnectionChange) {
        onConnectionChange(data.connected);
      }
    } catch (error) {
      console.error('[TikTok] Status check failed:', error);
      setStatus({ connected: false, loading: false });
    }
  };

  /**
   * Open TikTok OAuth popup
   * @param {boolean} direct - Whether to request direct posting scope
   */
  const connectTikTok = (direct = false) => {
    // Build OAuth URL
    const authUrl = `${baseUrl}/api/integrations/tiktok/login?tenantId=${encodeURIComponent(tenantId)}&direct=${direct ? '1' : '0'}`;
    
    // Open centered popup
    const width = 680;
    const height = 720;
    const left = window.top.outerWidth / 2 - width / 2;
    const top = window.top.outerHeight / 2 - height / 2;
    
    const popup = window.open(
      authUrl,
      'tiktok_auth',
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,status=no`
    );
    
    if (!popup) {
      setStatusMessage('❌ Popup blocked. Please allow popups for on-screen.app');
      return;
    }
    
    popup.focus?.();
    
    // Listen for messages from popup
    const handleMessage = (event) => {
      if (!event?.data) return;
      
      if (event.data.type === 'TK_OK') {
        console.log('[TikTok] Connection successful:', event.data);
        setStatusMessage('✅ TikTok connected successfully!');
        
        // Update status immediately
        setTimeout(() => {
          checkConnectionStatus();
        }, 500);
        
        // Clean up listener
        window.removeEventListener('message', handleMessage);
      } else if (event.data.type === 'TK_ERR') {
        console.error('[TikTok] Connection error:', event.data);
        const errorMsg = event.data.error || 'Connection failed';
        setStatusMessage(`❌ ${errorMsg}`);
        
        // Clean up listener
        window.removeEventListener('message', handleMessage);
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    // Also poll for popup close (fallback)
    const pollInterval = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollInterval);
        window.removeEventListener('message', handleMessage);
        checkConnectionStatus();
      }
    }, 500);
  };

  /**
   * Disconnect TikTok (currently just refreshes status)
   * In a full implementation, you might want a backend disconnect endpoint
   */
  const disconnectTikTok = async () => {
    if (window.confirm('Are you sure you want to disconnect TikTok? You will need to reconnect to post again.')) {
      // For now, just clear the status message
      // In production, you might call a backend /disconnect endpoint
      setStatusMessage('');
      setStatus({ connected: false, loading: false });
      if (onConnectionChange) {
        onConnectionChange(false);
      }
    }
  };

  // Loading state
  if (status.loading) {
    return (
      <div className="btn btn-secondary" disabled>
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Checking TikTok...
      </div>
    );
  }

  // Connected state
  if (status.connected) {
    return (
      <div className="d-flex flex-column gap-2">
        <div className="d-flex gap-2 align-items-center">
          <button 
            type="button" 
            className="btn btn-success d-flex align-items-center"
            disabled
          >
            ✓ TikTok Connected
            {status.isDirect && <span className="badge bg-light text-dark ms-2">Direct Post</span>}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={disconnectTikTok}
          >
            Disconnect
          </button>
        </div>
        {statusMessage && (
          <div className="alert alert-success mb-0 py-2">
            {statusMessage}
          </div>
        )}
        {status.expired && (
          <div className="alert alert-warning mb-0 py-2">
            ⚠️ Token expired. Please reconnect.
          </div>
        )}
      </div>
    );
  }

  // Not connected state
  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => connectTikTok(false)}
        >
          Connect TikTok (Upload)
        </button>
        {enableDirect && (
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => connectTikTok(true)}
            title="Requires video.publish scope - posts directly without review"
          >
            Connect (Direct Post)
          </button>
        )}
      </div>
      {statusMessage && (
        <div className={`alert ${statusMessage.startsWith('✅') ? 'alert-success' : 'alert-danger'} mb-0 py-2`}>
          {statusMessage}
        </div>
      )}
      <div className="text-muted small">
        <strong>Upload mode:</strong> Sends videos to your TikTok inbox as drafts for final review<br/>
        <strong>Direct mode:</strong> Posts immediately (requires TikTok app approval)
      </div>
    </div>
  );
}

export default TikTokConnectButton;